import { Children, isValidElement, type ComponentPropsWithoutRef, type ReactNode } from 'react'

import {
  paneResponsiveCardGridClassName,
  type PaneResponsiveCardGridColumnMode,
  type PaneResponsiveCardGridMaxColumns,
} from '@/components/layout/pane-responsive-card-grid-classes'
import { resolveEntityCardListMaxColumns } from '@/lib/resolve-entity-card-list-max-columns'

type PaneResponsiveCardGridProps = {
  children: ReactNode
  className?: string
  /** Cap columns at 2, 3, or 4. Defaults from tenant config. */
  maxColumns?: PaneResponsiveCardGridMaxColumns
  /**
   * Pane breakpoint family for when multi-column layout starts.
   * @default 'pane-wide' — `@2card` (900px pane); browse lists.
   * `pane-compact` — `@2col` (384px pane) on desktop; 1 column below `md`. Homepage Featured.
   */
  columnMode?: PaneResponsiveCardGridColumnMode
} & Omit<ComponentPropsWithoutRef<'ul'>, 'children' | 'className'>

/**
 * Reusable grid for card lists in the main page pane: width follows the **container** (see `App.tsx` `@container` scroll region).
 * Renders a semantic `ul`; each child is wrapped in an `li` grid item.
 */
export function PaneResponsiveCardGrid({
  children,
  className,
  maxColumns,
  columnMode = 'pane-wide',
  ...props
}: PaneResponsiveCardGridProps) {
  const resolvedMaxColumns = resolveEntityCardListMaxColumns(maxColumns)
  const items = Children.toArray(children).filter((child) => child != null)
  const singleItem = items.length === 1

  return (
    <ul
      className={paneResponsiveCardGridClassName(resolvedMaxColumns, className, columnMode)}
      {...props}
    >
      {items.map((child, index) => {
        const key = isValidElement(child) && child.key != null ? child.key : index
        return (
          <li key={key} className={singleItem ? 'col-span-full min-w-0' : 'min-w-0'}>
            {child}
          </li>
        )
      })}
    </ul>
  )
}

export {
  paneResponsiveCardGridClassName,
  type PaneResponsiveCardGridColumnMode,
  type PaneResponsiveCardGridMaxColumns,
} from '@/components/layout/pane-responsive-card-grid-classes'
